import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMissions } from '../services/missionService';
import chauffeurService from '../services/chauffeurService';
import dayjs from 'dayjs';
import './MissionCalendar.css';
import MissionGridCalendar from './MissionGridCalendar';
import { useI18n } from '../i18n/I18nContext';
import { useFilter } from '../context/FilterContext';

const MissionCalendar = () => {
  const { t } = useI18n();
  const { selectedTransporteurId } = useFilter();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [selectedMission, setSelectedMission] = useState(null);
  const [timeframe, setTimeframe] = useState([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [timelineWidth, setTimelineWidth] = useState(0);
  const [viewMode, setViewMode] = useState('timeline'); // timeline | grid
  const ganttContainerRef = useRef(null);
  const timelineHeaderRef = useRef(null);
  const rowScrollRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMissions = async () => {
      try {
        setLoading(true);
        const missionsData = await getMissions({ transporteurId: selectedTransporteurId });
        setMissions(Array.isArray(missionsData) ? missionsData : []);
        setError(null);
      } catch (err) {
        setError(err.message);
        setMissions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMissions();
  }, [selectedTransporteurId]);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const driverData = await chauffeurService.getAllDrivers(selectedTransporteurId);
        setDrivers(Array.isArray(driverData) ? driverData : []);
      } catch (e) {
        console.error('Failed to load drivers', e);
        setDrivers([]);
      }
    };
    fetchDrivers();
  }, [selectedTransporteurId]);

  useEffect(() => {
    const updateSizes = () => {
      if (ganttContainerRef.current) {
        setContainerWidth(ganttContainerRef.current.offsetWidth);
      }
      if (timelineHeaderRef.current) {
        setTimelineWidth(timelineHeaderRef.current.offsetWidth);
      }
    };
    updateSizes();
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
  }, []);

  useEffect(() => {
    if (timelineHeaderRef.current) {
      setTimelineWidth(timelineHeaderRef.current.offsetWidth);
    }
  }, [timeframe.length, containerWidth]);

  useEffect(() => {
    const monthStart = dayjs(`${selectedMonth}-01`).startOf('month');
    const monthEnd = monthStart.endOf('month');
    const days = [];
    let currentDate = monthStart;
    while (currentDate.isBefore(monthEnd) || currentDate.isSame(monthEnd, 'day')) {
      days.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }
    setTimeframe(days);
  }, [selectedMonth]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'EN_COURS': return '#10B981';
      case 'EN_ATTENTE': return '#F59E0B';
      case 'TERMINEE': return '#3B82F6';
      case 'ANNULEE': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const filteredMissions = useMemo(() => {
    if (!missions || missions.length === 0) return [];
    let filtered = missions;
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(mission => mission.status === statusFilter);
    }
    if (!timeframe.length) return [];
    const start = timeframe[0].startOf('day');
    const end = timeframe[timeframe.length - 1].endOf('day');
    filtered = filtered.filter(mission => {
      const missionStart = dayjs(mission.departureDate);
      const missionEnd = dayjs(mission.arrivalDate);
      return (missionStart.isBefore(end) && missionEnd.isAfter(start));
    });
    return filtered;
  }, [missions, statusFilter, timeframe]);

  const filteredMissionsGrid = useMemo(() => {
    if (!missions || missions.length === 0) return [];
    if (statusFilter === 'ALL') return missions;
    return missions.filter(mission => mission.status === statusFilter);
  }, [missions, statusFilter]);

  const setPresetThisMonth = () => {
    const now = dayjs().format('YYYY-MM');
    setSelectedMonth(now);
  };

  const columnTemplate = '170px 1.2fr 1.2fr 1fr 120px 110px';

  const handleSelectMission = (mission) => {
    setSelectedMission(mission?.id || null);
  };

  const renderTimelineView = () => (
    <div className="mission-calendar-container" ref={ganttContainerRef}>
      <div className="calendar-header">
        <h2>{t('gantt.calendar.title')}</h2>
        <p>{t('gantt.calendar.visualSchedule')}</p>
      </div>

      <div className="calendar-filters">
        <div className="filter-group">
          <label>{t('gantt.filters.status')}</label>
          <div className="filter-buttons">
            <button className={statusFilter === 'ALL' ? 'filter-button active' : 'filter-button'} onClick={() => setStatusFilter('ALL')}>
              {t('gantt.filters.allMissions')}
            </button>
            <button className={statusFilter === 'EN_ATTENTE' ? 'filter-button active' : 'filter-button'} onClick={() => setStatusFilter('EN_ATTENTE')}>
              {t('missions.status.pending')}
            </button>
            <button className={statusFilter === 'EN_COURS' ? 'filter-button active' : 'filter-button'} onClick={() => setStatusFilter('EN_COURS')}>
              {t('missions.status.in_progress')}
            </button>
            <button className={statusFilter === 'TERMINEE' ? 'filter-button active' : 'filter-button'} onClick={() => setStatusFilter('TERMINEE')}>
              {t('missions.status.completed')}
            </button>
            <button className={statusFilter === 'ANNULEE' ? 'filter-button active' : 'filter-button'} onClick={() => setStatusFilter('ANNULEE')}>
              {t('missions.status.cancelled')}
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>{t('gantt.filters.month')}</label>
          <div className="date-range-container" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>{/* 8px / 16 = 0.5rem */}
            <button className="filter-button" onClick={() => setSelectedMonth(dayjs(selectedMonth + '-01').subtract(1, 'month').format('YYYY-MM'))}>◀</button>
            <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="date-input" style={{ minWidth: '10rem' }} />{/* 160px / 16 = 10rem */}
            <button className="filter-button" onClick={setPresetThisMonth}>{t('gantt.filters.thisMonth')}</button>
            <button className="filter-button" onClick={() => setSelectedMonth(dayjs(selectedMonth + '-01').add(1, 'month').format('YYYY-MM'))}>▶</button>
          </div>
        </div>
      </div>

      <div className="calendar-stats">
        <div className="stat-item">
          <span className="stat-number">{viewMode === 'grid' ? filteredMissionsGrid.length : filteredMissions.length}</span>
          <span className="stat-label">{t('gantt.stats.visibleMissions')}</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{(viewMode === 'grid' ? filteredMissionsGrid : filteredMissions).filter(m => m.status === 'EN_COURS').length}</span>
          <span className="stat-label">{t('missions.status.in_progress')}</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{(viewMode === 'grid' ? filteredMissionsGrid : filteredMissions).filter(m => m.status === 'TERMINEE').length}</span>
          <span className="stat-label">{t('missions.status.completed')}</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{timeframe.length}</span>
          <span className="stat-label">{t('gantt.stats.daysShown')}</span>
        </div>
      </div>

      <div className="calendar-list-container">
        <div className="calendar-list-header">
          <div className="calendar-row-labels-header">{t('common.date')}</div>
          <div className="calendar-list-scroll-container" ref={timelineHeaderRef}>
            <div className="calendar-list-columns" style={{ gridTemplateColumns: columnTemplate }}>
              <div className="calendar-list-col"><div className="timeline-day-name">{t('gantt.table.from')}</div></div>
              <div className="calendar-list-col"><div className="timeline-day-name">{t('gantt.table.to')}</div></div>
              <div className="calendar-list-col"><div className="timeline-day-name">{t('gantt.table.time')}</div></div>
              <div className="calendar-list-col"><div className="timeline-day-name">{t('common.statusLabel')}</div></div>
              <div className="calendar-list-col"><div className="timeline-day-name">{t('gantt.table.actions')}</div></div>
            </div>
          </div>
        </div>
        <div className="calendar-rows-container">
          {(() => {
            const groups = {};
            filteredMissions.forEach((m) => {
              const key = dayjs(m.departureDate).format('YYYY-MM-DD');
              if (!groups[key]) groups[key] = [];
              groups[key].push(m);
            });
            const orderedDays = Object.keys(groups).sort();
            if (orderedDays.length === 0) return (
              <div className="calendar-empty-filter">
                <div className="empty-filter-icon">🔍</div>
                <h3>{t('gantt.empty.noMissions')}</h3>
                <p>{t('gantt.empty.tryFilters')}</p>
              </div>
            );
            return orderedDays.map((day) => (
              <div key={day} className="calendar-row" style={{ borderBottom: 'none' }}>
                <div className="calendar-row-label" style={{ background: '#F9FAFB' }}>
                  <div className="mission-info">
                    <h4 style={{ fontSize: 18, margin: 0 }}>{dayjs(day).format('dddd')}</h4>
                    <div className="mission-destination" style={{ fontWeight: 600, color: '#111827', fontSize: 20 }}>
                      {dayjs(day).format('MMM DD, YYYY')}
                    </div>
                  </div>
                </div>
                <div className="calendar-row-content" style={{ padding: '0.75rem' }}>{/* 12px / 16 = 0.75rem */}
                  {groups[day].map((mission) => (
                    <div key={mission.id} className="mission-ticket" style={{ display: 'grid', gridTemplateColumns: columnTemplate, gap: '0.75rem', alignItems: 'center', background: 'white', borderRadius: '0.75rem', border: '0.0625rem solid #E5E7EB', padding: '0.75rem 0.875rem', marginBottom: '0.75rem', boxShadow: '0 0.0625rem 0.125rem rgba(0,0,0,0.04)' }}>{/* 12px 1px 14px -> rem */}
                      <div className="ticket-date" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '0.625rem', padding: '0.625rem 0.5rem', width: '100%' }}>{/* 10px 8px -> rem */}
                        <span style={{ fontWeight: 700, fontSize: 38, color: '#111827' }}>{dayjs(mission.departureDate).format('DD')}</span>
                        <span style={{ color: '#6B7280', fontSize: 18 }}>{dayjs(mission.departureDate).format('MMM')}</span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>{t('gantt.table.from')}</div>
                        <div style={{ fontSize: 15, color: '#111827', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mission.departureLocation}</div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>{t('gantt.table.to')}</div>
                        <div style={{ fontSize: 15, color: '#111827', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mission.arrivalLocation}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>{t('gantt.table.time')}</div>
                        <div style={{ fontSize: 15, color: '#111827', fontWeight: 600 }}>{dayjs(mission.departureDate).format('HH:mm')} - {dayjs(mission.arrivalDate).format('HH:mm')}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span className="status-badge" style={{ background: getStatusColor(mission.status) }}>
                          {t(`missions.status.${mission.status.toLowerCase()}`)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="action-button" onClick={() => setSelectedMission(mission.id)}>{t('common.details')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="gantt-loading">
      <div className="loading-spinner"></div>
      <p>{t('common.loading')}</p>
    </div>
  );

  if (error) return (
    <div className="gantt-error">
      <div className="error-icon">⚠️</div>
      <h3>{t('common.error')}</h3>
      <p>{error}</p>
      <button className="retry-button" onClick={() => window.location.reload()}>{t('common.retry')}</button>
    </div>
  );

  return (
    <div className="mission-calendar-page">
      <div className="view-switch" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.75rem' }}>{/* 8px 12px -> rem */}
        <div className="btn-group" style={{ display: 'flex', gap: '0.5rem' }}>{/* 8px / 16 = 0.5rem */}
          <button className={viewMode === 'timeline' ? 'filter-button active' : 'filter-button'} onClick={() => setViewMode('timeline')}>Timeline</button>
          <button className={viewMode === 'grid' ? 'filter-button active' : 'filter-button'} onClick={() => setViewMode('grid')}>Grid</button>
        </div>
      </div>

      {(viewMode === 'grid' ? filteredMissionsGrid : filteredMissions).length === 0 ? (
        <div className="calendar-empty-filter">
          <div className="empty-filter-icon">🔍</div>
          <h3>{t('gantt.empty.noMissions')}</h3>
          <p>{t('gantt.empty.tryFilters')}</p>
          <button className="secondary-button" onClick={() => { setStatusFilter('ALL'); setSelectedMonth(dayjs().format('YYYY-MM')); }}>
            {t('gantt.empty.reset')}
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <MissionGridCalendar
              missions={filteredMissionsGrid}
              drivers={drivers}
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
              onSelectMission={handleSelectMission}
            />
          ) : (
            renderTimelineView()
          )}
        </>
      )}

      {selectedMission && (
        <>
          <div className="gantt-details-backdrop" onClick={() => setSelectedMission(null)}></div>
          <div className="gantt-details-panel">
            <div className="details-panel-header">
              <h3>{t('gantt.details.title')}</h3>
              <div className="details-header-actions">
                {(() => {
                  const mission = (viewMode === 'grid' ? filteredMissionsGrid : filteredMissions).find(m => m.id === selectedMission);
                  if (!mission) return null;
                  return (
                    <button className="action-button primary" onClick={() => navigate(`/missions/edit/${mission.id}`)}>
                      {t('gantt.details.viewAll')}
                    </button>
                  );
                })()}
                <button className="close-details" onClick={() => setSelectedMission(null)} aria-label="Close" title="Close">×</button>
              </div>
            </div>
            {(() => {
              const mission = filteredMissions.find(m => m.id === selectedMission);
              if (!mission) return null;
              return (
                <div className="details-panel-content">
                  <div className="detail-section">
                    <h4>{t('gantt.details.routeInfo')}</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">{t('gantt.table.from')}</span>
                        <span className="detail-value">{mission.departureLocation}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t('gantt.table.to')}</span>
                        <span className="detail-value">{mission.arrivalLocation}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t('missions.form.departureDate')}</span>
                        <span className="detail-value">{dayjs(mission.departureDate).format('YYYY-MM-DD HH:mm')}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t('missions.form.arrivalDate')}</span>
                        <span className="detail-value">{dayjs(mission.arrivalDate).format('YYYY-MM-DD HH:mm')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>{t('gantt.details.cargoInfo')}</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">{t('missions.fields.cargoType')}</span>
                        <span className="detail-value">{mission.cargoType}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t('common.weight')}</span>
                        <span className="detail-value">{mission.weight} kg</span>
                      </div>
                    </div>
                  </div>
                  {mission.affectationMission && (
                    <div className="detail-section">
                      <h4>{t('gantt.details.assignmentInfo')}</h4>
                      <div className="detail-grid">
                        {mission.affectationMission.chauffeur && (
                          <div className="detail-item">
                            <span className="detail-label">{t('gantt.details.driver')}</span>
                            <span className="detail-value">
                              {mission.affectationMission.chauffeur.nom || mission.affectationMission.chauffeur.firstname || 'Unknown'}
                              {mission.affectationMission.chauffeur.prenom || mission.affectationMission.chauffeur.lastname ? ' ' + (mission.affectationMission.chauffeur.prenom || mission.affectationMission.chauffeur.lastname) : ''}
                            </span>
                          </div>
                        )}
                        {mission.affectationMission.vehicule && (
                          <div className="detail-item">
                            <span className="detail-label">{t('gantt.details.vehicle')}</span>
                            <span className="detail-value">{mission.affectationMission.vehicule.immatriculation}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default MissionCalendar;
